import React, { useState, useEffect, useContext } from 'react';
import { Tab, Tabs, Form, Card, Badge, Spinner, Alert, Modal, Button } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { backendUrl } from '../utils/variables';
import { MyContext } from "../MainContext";
import Header from '../components/header_home';
import Footer from '../components/footer';
import DeleteModal from '../components/delete';
import { timeAgo } from '../utils/functions';
import { useNavigate } from 'react-router-dom';

const PrayerRequestsPage = () => {

    const navigate = useNavigate();

  const {setUser, user, setCurrentInsight, adminData} = useContext(MyContext);
  const isAdmin = !!(adminData && adminData.role);

  // States for prayer request form
  const [prayerText, setPrayerText] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // States for prayer lists
  const [communityPrayers, setCommunityPrayers] = useState([]);
  const [myPrayers, setMyPrayers] = useState([]);
  const [testimonies, setTestimonies] = useState([]);
  const [loadingCommunity, setLoadingCommunity] = useState(true);
  const [loadingMine, setLoadingMine] = useState(true);
  const [loadingTestimonies, setLoadingTestimonies] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // State for testimony form
  const [showTestimonyForm, setShowTestimonyForm] = useState(null);
  const [testimonyText, setTestimonyText] = useState('');
  const [submittingTestimony, setSubmittingTestimony] = useState(false);

  // States for encouragement modal
  const [showEncouragementModal, setShowEncouragementModal] = useState(false);
  const [selectedPrayerId, setSelectedPrayerId] = useState(null);
  const [encouragementText, setEncouragementText] = useState('');
  const [submittingEncouragement, setSubmittingEncouragement] = useState(false);

  // New states for responses modal
  const [showResponsesModal, setShowResponsesModal] = useState(false);
  const [selectedPrayerResponses, setSelectedPrayerResponses] = useState([]);
  const [selectedPrayerTitle, setSelectedPrayerTitle] = useState('');

  // Admin delete
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [prayerToDelete, setPrayerToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteMsg, setDeleteMsg] = useState({});

  // Fetch community prayers (random prayers from others)
  const fetchCommunityPrayers = async () => {
    if(!user.user_id) {
      return;
    }

    try {
      setLoadingCommunity(true);
      setLoadError(null);
      
      const response = await fetch(`${backendUrl}/fetch_community_prayers.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.user_id,
          limit: 10
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setCommunityPrayers(data.prayers);
      } else {
        setLoadError(data.message || 'Failed to load community prayers');
      }
    } catch (err) {
      setLoadError('Network error. Please try again later.');
      console.error('Error fetching community prayers:', err);
    } finally {
      setLoadingCommunity(false);
    }
  };

  // Fetch my prayer requests
  const fetchMyPrayers = async () => {
    if(!user.user_id) {
      return;
    }

    try {
      setLoadingMine(true);
      
      const response = await fetch(`${backendUrl}/my_prayers.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.user_id
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setMyPrayers(data.prayers);
        //console.log('My prayers:', data);
      } else {
        console.error('Failed to load my prayers:', data.message);
      }
    } catch (err) {
      console.error('Error fetching my prayers:', err); 
    } finally {
      setLoadingMine(false);
    }
  };

  // Fetch prayers I've prayed for that now have testimonies
  const fetchTestimonies = async () => {
    if(!user.user_id) {
      return;
    }

    try {
      setLoadingTestimonies(true);
      
      const response = await fetch(`${backendUrl}/fetch_testimonies.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.user_id
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setTestimonies(data.prayers);
      } else {
        console.error('Failed to load testimonies:', data.message);
      }
    } catch (err) {
      console.error('Error fetching testimonies:', err); 
    } finally {
      setLoadingTestimonies(false);
    }
  };

  // Submit a new prayer request
  const handleSubmitPrayer = async (e) => {
    if(!user.user_id) {
      return;
    }

    e.preventDefault();
    
    if (prayerText.trim() === '') return;
    if (charCount > 1000) return;
    
    try {
      setIsSubmitting(true);
      setSubmitError(null);
      
      const response = await fetch(`${backendUrl}/create_prayer.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.user_id,
          prayer_text: prayerText,
          is_anonymous: true
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setPrayerText('');
        setIsAnonymous(false);
        setCharCount(0);
        setSubmitSuccess(true);
        
        // Refresh my prayers list
        fetchMyPrayers();
        
        // Hide success message after 3 seconds
        setTimeout(() => {
          setSubmitSuccess(false);
        }, 3000);
      } else {
        setSubmitError(data.message || 'Failed to submit prayer request');
      }
    } catch (err) {
      setSubmitError('Network error. Please try again later.');
      console.error('Error submitting prayer:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle clicking on "Pray" button
  const handlePrayClick = async (prayerId) => {
    if(!user.user_id) {
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/pray.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.user_id,
          prayer_id: prayerId,
          encouragement: "" // No encouragement message
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Update prayer count in the community prayers list
        setCommunityPrayers(prevPrayers => 
          prevPrayers.map(prayer => 
            prayer.id === prayerId 
              ? { ...prayer, prayer_count: (parseInt(prayer.prayer_count) || 0) + 1, has_prayed: true } 
              : prayer
          )
        );
      }
    } catch (err) {
      console.error('Error registering prayer:', err);
    }
  };

  // Handle opening encouragement modal
  const handleOpenEncouragementModal = (prayerId) => {
    setSelectedPrayerId(prayerId);
    setEncouragementText('');
    setShowEncouragementModal(true);
  };

  // Handle submitting encouragement
  const handleSubmitEncouragement = async () => {
    if(!user.user_id || !selectedPrayerId) {
      return;
    }

    if (encouragementText.trim() === '') return;
    
    try {
      setSubmittingEncouragement(true);
      
      const response = await fetch(`${backendUrl}/pray.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.user_id,
          prayer_id: selectedPrayerId,
          encouragement: encouragementText
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Update prayer count in the community prayers list
        setCommunityPrayers(prevPrayers => 
          prevPrayers.map(prayer => 
            prayer.id === selectedPrayerId 
              ? { ...prayer, prayer_count: (parseInt(prayer.prayer_count) || 0) + 1, has_prayed: true } 
              : prayer
          )
        );
        
        // Close modal and reset form
        setShowEncouragementModal(false);
        setSelectedPrayerId(null);
        setEncouragementText('');
      }
    } catch (err) {
      console.error('Error sending encouragement:', err);
    } finally {
      setSubmittingEncouragement(false);
    }
  };

  // Submit testimony
  const handleSubmitTestimony = async (prayerId) => {
    if(!user.user_id) {
      return;
    }

    if (testimonyText.trim() === '') return;
    
    try {
      setSubmittingTestimony(true);
      
      const response = await fetch(`${backendUrl}/add_testimony.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.user_id,
          prayer_id: prayerId,
          testimony: testimonyText
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Update the prayer in my prayers list
        setMyPrayers(prevPrayers => 
          prevPrayers.map(prayer => 
            prayer.id === prayerId 
              ? { ...prayer, testimony: testimonyText } 
              : prayer
          )
        );
        
        // Reset form state
        setShowTestimonyForm(null);
        setTestimonyText('');
      }
    } catch (err) {
      console.error('Error adding testimony:', err);
    } finally {
      setSubmittingTestimony(false);
    }
  };

  // Handle text change in prayer input
  const handlePrayerTextChange = (e) => {
    const text = e.target.value;
    setPrayerText(text);
    setCharCount(text.length);
  };

  // Handle opening responses modal
  const handleOpenResponsesModal = (prayer) => {
    setSelectedPrayerResponses(prayer.responses || []);
    setSelectedPrayerTitle(prayer.prayer_text);
    setShowResponsesModal(true);
  };

  const confirmDeletePrayer = (prayer) => {
    setPrayerToDelete(prayer);
    setDeleteMsg({});
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (isDeleting) return;
    setShowDeleteModal(false);
    setPrayerToDelete(null);
    setDeleteMsg({});
  };

  const deletePrayer = async (prayerId) => {
    if (!isAdmin || isDeleting || !prayerId) return;
    const adminEmail = adminData?.email || user?.email;
    if (!adminEmail) {
      setDeleteMsg({ mode: 'danger', message: 'Admin email not available.' });
      return;
    }

    try {
      setIsDeleting(true);
      setDeleteMsg({});
      const response = await fetch(`${backendUrl}/delete_prayer.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prayer_id: prayerId,
          email: adminEmail,
        }),
      });
      const data = await response.json();
      if (data.success) {
        setCommunityPrayers((prev) => prev.filter((p) => p.id !== prayerId));
        setMyPrayers((prev) => prev.filter((p) => p.id !== prayerId));
        setTestimonies((prev) => prev.filter((p) => p.id !== prayerId));
        setDeleteMsg({ mode: 'success', message: 'Prayer request deleted.' });
        setTimeout(() => {
          setShowDeleteModal(false);
          setPrayerToDelete(null);
          setDeleteMsg({});
          setIsDeleting(false);
        }, 800);
      } else {
        setDeleteMsg({ mode: 'danger', message: data.message || 'Failed to delete prayer request.' });
        setIsDeleting(false);
      }
    } catch (err) {
      console.error('Error deleting prayer:', err);
      setDeleteMsg({ mode: 'danger', message: 'Network error. Please try again.' });
      setIsDeleting(false);
    }
  };

  // Handle username click
  const handleUsernameClick = (username) => {
    navigate(`/${username}`)
  };

  // Set up auto-refresh for community prayers every 5 minutes
  useEffect(() => {
    fetchCommunityPrayers();
    fetchMyPrayers();
    fetchTestimonies();
    
    const refreshInterval = setInterval(() => {
      fetchCommunityPrayers();
    }, 5 * 60 * 1000); // 5 minutes
    
    return () => clearInterval(refreshInterval);
  }, [user?.user_id]);

  // Render prayer card
  const renderPrayerCard = (prayer, isMyPrayer = false) => (
    <Card className="mb-3" key={prayer.id}>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div>
                <Card.Subtitle className="text-muted mb-1 d-none" style={{ fontSize: '0.9rem' }}>
                  {prayer.is_anonymous ? 'Anonymous' : prayer.username}
                </Card.Subtitle>
                <Card.Subtitle className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>
                {timeAgo(prayer.created_at)}
                </Card.Subtitle>
          </div>

          <div className="d-flex align-items-center">
            <Badge bg="info" className="me-2">
              {prayer.view_count || 0} View{`${prayer.view_count > 1 ? 's' : ''}`}
            </Badge>
            <Badge bg="warning" className="me-2">
              {prayer.prayer_count || 0} Prayer{`${prayer.prayer_count > 1 ? 's' : ''}`}
            </Badge>
            {prayer.responses && prayer.responses.length > 0 && (
              <Badge 
                bg="success" 
                className="me-2"
                style={{ cursor: 'pointer' }}
                onClick={() => handleOpenResponsesModal(prayer)}
              >
                {prayer.responses.length} Message{`${prayer.responses.length > 1 ? 's' : ''}`}
              </Badge>
            )}
            {isAdmin && (
              <button
                type="button"
                className="btn btn-link text-danger p-0 ms-1"
                title="Delete prayer request"
                aria-label="Delete prayer request"
                onClick={() => confirmDeletePrayer(prayer)}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            )}
          </div>
        </div>
        
        <Card.Text>{prayer.prayer_text}</Card.Text>
        
        {prayer.testimony && (
          <Alert variant="success" className="mt-3 mb-2">
            <strong>Testimony:</strong> {prayer.testimony}
          </Alert>
        )}
        
        <div className="d-flex justify-content-between align-items-center mt-3">
          {isMyPrayer ? (
            <>
              {!prayer.testimony && (
                <button 
                  className="btn b-o-primary btn-sm"
                  onClick={() => setShowTestimonyForm(prayer.id)}
                >
                  Add Testimony
                </button>
              )}
              <div>
                {prayer.responses && prayer.responses.length > 0 && 3 > 4 && (
                  <button 
                    className="btn btn-outline-success btn-sm"
                    onClick={() => handleOpenResponsesModal(prayer)}
                  >
                    View Messages
                  </button>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="d-flex">
                <button 
                  className={`btn btn-sm ${prayer.has_prayed ? "b-o-primary" : "b-primary"} me-2`}
                  onClick={() => handlePrayClick(prayer.id)}
                  disabled={prayer.has_prayed}
                >
                  {prayer.has_prayed ? "You Prayed" : "Pray"}
                </button>

                {!prayer.has_prayed && (
                  <button 
                    className="btn btn-sm b-o-primary"
                    onClick={() => handleOpenEncouragementModal(prayer.id)}
                  >
                    Encourage
                  </button>
                )}
              </div>

            </>
          )}
        </div>
        
        {showTestimonyForm === prayer.id && (
          <div className="mt-3">
            <Form.Group>
              <Form.Label>Your Testimony</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={testimonyText}
                onChange={(e) => setTestimonyText(e.target.value)}
                placeholder="Share how God answered this prayer..."
              />
            </Form.Group>
            <div className="d-flex justify-content-end mt-2">
              <button 
                className="btn btn-dark btn-sm me-2"
                onClick={() => {
                  setShowTestimonyForm(null);
                  setTestimonyText('');
                }}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary b-primary btn-sm"
                onClick={() => handleSubmitTestimony(prayer.id)}
                disabled={submittingTestimony || testimonyText.trim() === ''}
              >
                {submittingTestimony ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-1" />
                    Submitting...
                  </>
                ) : (
                  'Submit Testimony'
                )}
              </button>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );

  return (
    <div className="container py-4 pr_c">
      <Header title="Prayer Requests" />
      <h2 className="mb-4">Prayer Requests</h2>
      <p className='p-hd'>Join your faith with other members of the Clowza community in prayer as we anticipate remarkable testimonies for you and everyone. Together, let's uplift each other and strengthen our bond as one body in christ.</p>

      {/* Prayer Request Form */}
      <Card className="mb-4">
        <Card.Body>
          <Form onSubmit={handleSubmitPrayer}>
            <Form.Group className="mb-3">
              <Form.Label>Your Prayer Request</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={prayerText}
                onChange={handlePrayerTextChange}
                placeholder="Share your prayer request here..."
                maxLength={1000}
              />
              <div className="d-flex justify-content-between mt-1 d-none">
                <Form.Check
                  type="checkbox"
                  label="Post anonymously"
                  checked={isAnonymous}
                  onChange={(e) => setIsAnonymous(e.target.checked)}
                />
                <small className={charCount > 900 ? (charCount > 1000 ? "text-danger" : "text-warning") : "text-muted"}>
                  {charCount}/1000 characters
                </small>
              </div>
            </Form.Group>

            {submitError && (
              <Alert variant="danger" className="my-2">
                {submitError}
              </Alert>
            )}
            
            {submitSuccess && (
              <Alert variant="success" className="my-2">
                Your prayer request has been submitted successfully!
              </Alert>
            )}
            
            <div className="d-flex justify-content-end">
              <button 
                type="submit" 
                className="btn btn-primary b-primary"
                disabled={isSubmitting || prayerText.trim() === '' || charCount > 1000}
              >
                {isSubmitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-1" />
                    Submitting...
                  </>
                ) : (
                  'Submit Prayer Request'
                )}
              </button>
            </div>
          </Form>
        </Card.Body>
      </Card>
      
      {/* Tabs with horizontal scrolling for mobile */}
    
      <div className="scrollable-tabs-container">
      <style>
    {`
    .scrollable-tabs-container {
        margin-bottom:60px;
      }
      .scrollable-tabs-container .nav-tabs {
        flex-wrap: nowrap;
        overflow-x: auto;
        scrollbar-width: none;  /* Firefox */
        white-space: nowrap;
      }
      
      .scrollable-tabs-container .nav-tabs::-webkit-scrollbar {
        display: none;  /* Chrome, Safari, Edge */
      }
      
      .scrollable-tabs-container .nav-item {
        float: none;
        display: inline-block;
      }
      
      /* Ensure tab content doesn't scroll horizontally */
      .tab-content {
        overflow-x: visible;
      }
      
      /* Style for the response cards in modal */
      .response-card {
        border-left: 3px solid #6c757d;
        margin-bottom: 15px;
        padding-left: 10px;
      }
      
      .response-username {
        color: #48887b;
        cursor: pointer;
        font-weight: 500;
      }
      
      .response-username:hover {
        text-decoration: underline;
      }
      
      .response-date {
        font-size: 0.8rem;
        color: #6c757d;
        margin-bottom: 5px;
      }
      
      .response-message {
        color: #212529;
      }
    `}
  </style>

        <Tabs defaultActiveKey="community" className="mb-3">
          <Tab eventKey="community" title="Prayer Requests">
            {loadingCommunity ? (
              <div className="text-center py-5">
                <Spinner animation="border" />
                <p className="mt-2">Loading prayer requests...</p>
              </div>
            ) : loadError ? (
              <Alert variant="danger">{loadError}</Alert>
            ) : communityPrayers.length === 0 ? (
              <div className="text-center py-5">
                <p>No prayer requests available at the moment.</p>
                <button className="btn b-o-primary" onClick={fetchCommunityPrayers}>
                  Refresh
                </button>
              </div>
            ) : (
              <>
                <div className="mb-3 text-end">
                  <button className="btn b-o-primary btn-sm" onClick={fetchCommunityPrayers}>
                    Refresh
                  </button>
                </div>
                {communityPrayers.map(prayer => renderPrayerCard(prayer))}
              </>
            )}
          </Tab>

          <Tab eventKey="testimonies" title="Testimonies">
            {loadingTestimonies ? (
              <div className="text-center py-5">
                <Spinner animation="border" />
                <p className="mt-2">Loading testimonies...</p>
              </div>
            ) : testimonies.length === 0 ? (
              <div className="text-center py-5">
                <p>No testimonies available yet for prayers you've prayed for.</p>
                <button className="btn b-o-primary" onClick={fetchTestimonies}>
                  Refresh
                </button>
              </div>
            ) : (
              <>
                <div className="mb-3 text-end">
                  <button className="btn b-o-primary btn-sm" onClick={fetchTestimonies}>
                    Refresh
                  </button>
                </div>
                {testimonies.map(prayer => renderPrayerCard(prayer))}
              </>
            )}
          </Tab>
          
          <Tab eventKey="my" title="My Prayer Requests">
            {loadingMine ? (
              <div className="text-center py-5">
                <Spinner animation="border" />
                <p className="mt-2">Loading your prayer requests...</p>
              </div>
            ) : myPrayers.length === 0 ? (
              <div className="text-center py-5">
                <p>You haven't submitted any prayer requests yet.</p>
              </div>
            ) : (
              <>
                <div className="mb-3 text-end">
                  <button className="btn b-o-primary btn-sm" onClick={fetchMyPrayers}>
                    Refresh
                  </button>
                </div>
                {myPrayers.map(prayer => renderPrayerCard(prayer, true))}
              </>
            )}
          </Tab>
        </Tabs>
      </div>

      {/* Encouragement Modal */}
      <Modal show={showEncouragementModal} onHide={() => setShowEncouragementModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Send Words of Encouragement</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Your Message of Encouragement</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={encouragementText}
              onChange={(e) => setEncouragementText(e.target.value)}
              placeholder="Share an encouraging message..."
              maxLength={700}
            />
            <small className="text-muted d-block text-end mt-1">
              {encouragementText.length}/700 characters
            </small>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <button 
            className="btn bg-dark btn-sm text-white" 
            onClick={() => setShowEncouragementModal(false)}
          >
            Cancel
          </button>
          <button 
            className="btn b-primary btn-sm" 
            onClick={handleSubmitEncouragement}
            disabled={submittingEncouragement || encouragementText.trim() === ''}
          >
            {submittingEncouragement ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Sending...
              </>
            ) : (
              'Send Encouragement'
            )}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Responses Modal */}
      <Modal 
        show={showResponsesModal} 
        onHide={() => setShowResponsesModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Messages</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">Prayer: {selectedPrayerTitle}</p>
          
          {selectedPrayerResponses.length === 0 ? (
            <p className="text-center">No messages yet for this prayer request.</p>
          ) : (
            selectedPrayerResponses.map(response => (
              <div className="response-card" key={response.id}>
                <div className="response-date">{timeAgo(response.created_at)}</div>
                <div 
                  className="response-username" 
                  onClick={() => handleUsernameClick(response.username)}
                >
                  {`@${response.username}`}

                </div>
                <div className="response-message mt-1">{response.message}</div>
              </div>
            ))
          )}
        </Modal.Body>
        <Modal.Footer>
          <button 
            className="btn b-primary btn-sm" 
            onClick={() => setShowResponsesModal(false)}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>

      {showDeleteModal && (
        <DeleteModal
          text={'Are you sure you want to delete this prayer request? This action cannot be undone.'}
          close={closeDeleteModal}
          delete_fn={deletePrayer}
          id={prayerToDelete?.id}
          del_msg={deleteMsg}
        />
      )}

      <Footer page="prayer" />
    </div>
  );
};

export default PrayerRequestsPage;